import React, { useEffect, useRef } from 'react';
import { AxiosError, AxiosRequestConfig } from 'axios';
import axiosClient from "@/shared/lib/axios/axios.ts";
import convertDateTimeStringsToDate from "@/shared/utils/date/convert-datetime.tsx";
import { useSession } from "@/auth/contexts/auth-context.tsx";
import { refreshToken as refreshTokenRequest } from "@/auth/api/requests/auth.requests.ts";
import { useNavigate } from "react-router-dom";
import { AUTH_URL } from "@/auth/routes.ts";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { useGetOrganizations } from "@/auth/api/queries/user.queries";

type TErrorHandlerProps = {
  children?: React.ReactNode;
};

// Module-level state for refresh coordination (outside React lifecycle)
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const AxiosErrorHandler = ({ children }: TErrorHandlerProps) => {
  const { accessToken, refreshToken: storedRefreshToken, signOut, setAccessToken } = useSession();
  const navigate = useNavigate();
  const { selectedOrganizationId, setSelectedOrganizationId } = useOrganizationStore();
  const { data: orgsData } = useGetOrganizations();

  // Use refs to store current values - these can be read in interceptors without stale closure issues
  const accessTokenRef = useRef<string | null>(accessToken);
  const refreshTokenRef = useRef<string | null>(storedRefreshToken);
  const organizationIdRef = useRef<number | null>(selectedOrganizationId);
  const signOutRef = useRef(signOut);
  const setAccessTokenRef = useRef(setAccessToken);
  const navigateRef = useRef(navigate);

  // Keep refs in sync with state
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    refreshTokenRef.current = storedRefreshToken;
  }, [storedRefreshToken]);

  useEffect(() => {
    organizationIdRef.current = selectedOrganizationId;
  }, [selectedOrganizationId]);

  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  useEffect(() => {
    setAccessTokenRef.current = setAccessToken;
  }, [setAccessToken]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Initialize default organization if none selected
  useEffect(() => {
    const firstOrgId = orgsData?.data?.[0]?.id;
    if (!selectedOrganizationId && firstOrgId) {
      setSelectedOrganizationId(firstOrgId);
    }
  }, [orgsData?.data, selectedOrganizationId, setSelectedOrganizationId]);

  // Set up interceptors ONCE on mount
  useEffect(() => {
    console.log('[AxiosErrorHandler] Setting up interceptors (once)');

    // Helper function to perform token refresh (coordinated across all requests)
    const performTokenRefresh = async (): Promise<string | null> => {
      const currentRefreshToken = refreshTokenRef.current;

      if (!currentRefreshToken) {
        console.log('[AxiosErrorHandler] No refresh token available');
        return null;
      }

      try {
        console.log('[AxiosErrorHandler] Refreshing access token...');
        // Uses authAxiosClient internally - no interceptor loop
        const res = await refreshTokenRequest({ refresh: currentRefreshToken });
        const newAccessToken = res?.data.access;
        const newRefreshToken = res?.data?.refresh;

        // Update tokens silently via localStorage first, then state
        if (newAccessToken) {
          localStorage.setItem('maestro-access-token', newAccessToken);
          accessTokenRef.current = newAccessToken; // Update ref immediately
        }
        if (newRefreshToken) {
          localStorage.setItem('maestro-refresh-token', newRefreshToken);
          refreshTokenRef.current = newRefreshToken; // Update ref immediately
        }

        // Update React state (won't cause remount since tokens are the same type)
        if (newAccessToken) {
          setAccessTokenRef.current(newAccessToken);
        }

        console.log('[AxiosErrorHandler] Token refresh successful');
        return newAccessToken;
      } catch (error) {
        console.error('[AxiosErrorHandler] Token refresh failed:', error);
        return null;
      }
    };

    // Coordinated refresh - ensures only one refresh happens at a time
    const coordinatedRefresh = async (): Promise<string | null> => {
      if (isRefreshing && refreshPromise) {
        // Another request is already refreshing - wait for it
        console.log('[AxiosErrorHandler] Waiting for ongoing refresh...');
        return refreshPromise;
      }

      isRefreshing = true;
      refreshPromise = performTokenRefresh().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });

      return refreshPromise;
    };

    // REQUEST INTERCEPTOR
    const requestInterceptor = axiosClient.interceptors.request.use(
      async (config) => {
        // Read current token from ref - always up to date, no stale closures
        const tokenToUse = accessTokenRef.current;

        // Always add the token to headers if available
        if (tokenToUse && config.headers) {
          config.headers.Authorization = `Bearer ${tokenToUse}`;
          console.log('[AxiosErrorHandler] Added Authorization header to request:', config.url);
        } else {
          console.warn('[AxiosErrorHandler] No token available for request:', config.url);
        }

        // Add organization header if available
        const currentOrgId = organizationIdRef.current;
        if (currentOrgId && config.headers) {
          (config.headers as any)["X-Minemaestro-Organization"] = String(currentOrgId);
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // RESPONSE INTERCEPTOR
    const responseInterceptor = axiosClient.interceptors.response.use(
      (response) => convertDateTimeStringsToDate(response),
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Handle 401 Unauthorized errors with token refresh
        if (error.response?.status === 401) {
          // No refresh token available - logout immediately
          if (!refreshTokenRef.current) {
            console.log('[AxiosErrorHandler] No refresh token, signing out');
            signOutRef.current();
            navigateRef.current(AUTH_URL.SIGN);
            return Promise.reject(error);
          }

          // Try to refresh token only once per request (prevent infinite loops)
          if (!originalRequest._retry) {
            originalRequest._retry = true;

            const newAccessToken = await coordinatedRefresh();

            if (newAccessToken) {
              // Retry the original request with new token
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: `Bearer ${newAccessToken}`,
              };
              const currentOrgId = organizationIdRef.current;
              if (currentOrgId) {
                (originalRequest.headers as any)["X-Minemaestro-Organization"] = String(currentOrgId);
              }

              return axiosClient(originalRequest);
            } else {
              // Token refresh failed - logout and redirect to login
              console.log('[AxiosErrorHandler] Refresh failed, signing out');
              signOutRef.current();
              navigateRef.current(AUTH_URL.SIGN);
              return Promise.reject(error);
            }
          } else {
            // Already tried to refresh once - logout and redirect
            console.log('[AxiosErrorHandler] Already retried, signing out');
            signOutRef.current();
            navigateRef.current(AUTH_URL.SIGN);
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axiosClient.interceptors.request.eject(requestInterceptor);
      axiosClient.interceptors.response.eject(responseInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - interceptors are set up once and read from refs

  return <>{children}</>;
};

export default AxiosErrorHandler;
